import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Plus,
  Search,
  Filter,
  Image as ImageIcon,
  Video,
  Trash2,
  Edit,
  Download,
  ExternalLink,
  Eye,
  Upload,
  Grid3X3,
  List,
  LayoutGrid,
  Loader2,
  X,
  Check,
  Folder,
  Tag,
  Calendar,
  FileType
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const MEDIA_CATEGORIES = [
  { id: 'all', name: 'Tous', icon: Grid3X3 },
  { id: 'logo', name: 'Logos', icon: ImageIcon },
  { id: 'print', name: 'Print', icon: FileType },
  { id: 'web', name: 'Web', icon: LayoutGrid },
  { id: 'video', name: 'Vidéos', icon: Video },
  { id: 'photo', name: 'Photos', icon: ImageIcon }
];

const GalleryPage = () => {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    url: '',
    thumbnail: '',
    category: 'logo',
    tags: '',
    case_study_id: '',
    type: 'image'
  });

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const res = await api.get('/website/gallery');
      setItems(res.data);
    } catch (error) {
      // Mock data
      setItems([
        { id: 'g1', title: 'Logo TechCorp', url: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800', thumbnail: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=400', category: 'logo', tags: ['tech', 'startup'], type: 'image', case_study_id: 'case-1', created_at: '2024-06-15' },
        { id: 'g2', title: 'Brochure GreenLife', url: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800', thumbnail: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=400', category: 'print', tags: ['eco', 'print'], type: 'image', case_study_id: 'case-2', created_at: '2024-07-20' },
        { id: 'g3', title: 'Site RetailMax', url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800', thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400', category: 'web', tags: ['ecommerce', 'retail'], type: 'image', case_study_id: 'case-3', created_at: '2024-08-10' },
        { id: 'g4', title: 'Spot TV FoodCo', url: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800', thumbnail: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=400', category: 'video', tags: ['pub', 'food'], type: 'video', case_study_id: '', created_at: '2024-09-05' },
        { id: 'g5', title: 'Shooting corporate', url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800', thumbnail: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400', category: 'photo', tags: ['portrait', 'equipe'], type: 'image', case_study_id: '', created_at: '2024-09-15' },
        { id: 'g6', title: 'Logo FinanceHub', url: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800', thumbnail: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400', category: 'logo', tags: ['finance', 'bank'], type: 'image', case_study_id: '', created_at: '2024-10-01' },
        { id: 'g7', title: 'App HealthTrack', url: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800', thumbnail: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400', category: 'web', tags: ['app', 'sante'], type: 'image', case_study_id: '', created_at: '2024-10-15' },
        { id: 'g8', title: 'Catalogue SportMax', url: 'https://images.unsplash.com/photo-1541544537156-7627a7a4aa1c?w=800', thumbnail: 'https://images.unsplash.com/photo-1541544537156-7627a7a4aa1c?w=400', category: 'print', tags: ['sport', 'catalogue'], type: 'image', case_study_id: '', created_at: '2024-11-01' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    try {
      await api.post('/website/gallery', {
        ...uploadForm,
        tags: uploadForm.tags.split(',').map(t => t.trim()).filter(Boolean)
      });
      toast.success('Média ajouté !');
      fetchGalleryItems();
    } catch (error) {
      // Mock
      setItems(prev => [...prev, {
        ...uploadForm,
        id: `g-${Date.now()}`,
        tags: uploadForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        created_at: new Date().toISOString()
      }]);
      toast.success('Média ajouté !');
    }
    setShowUploadModal(false);
    setUploadForm({ title: '', url: '', thumbnail: '', category: 'logo', tags: '', case_study_id: '', type: 'image' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce média ?')) return;
    try {
      await api.delete(`/website/gallery/${id}`);
    } catch (e) {}
    setItems(prev => prev.filter(i => i.id !== id));
    toast.success('Média supprimé');
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a365d]">Galerie de réalisations</h1>
            <p className="text-[#64748b]">Médiathèque de tous vos projets</p>
          </div>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
          >
            <Upload className="w-4 h-4 mr-2" />
            Ajouter un média
          </Button>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
              <Input
                placeholder="Rechercher par titre ou tag..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 items-center">
              <div className="flex bg-slate-100 rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="h-8 w-8 p-0"
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-8 w-8 p-0"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex gap-2 mt-4 flex-wrap">
            {MEDIA_CATEGORIES.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={cn(
                  selectedCategory === cat.id && "bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                )}
              >
                <cat.icon className="w-3 h-3 mr-1" />
                {cat.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredItems.map(item => (
              <div
                key={item.id}
                className="group relative bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => setSelectedItem(item)}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={item.thumbnail || item.url}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {item.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                        <Video className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="font-medium text-sm text-[#1a365d] truncate">{item.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{item.category}</Badge>
                  </div>
                </div>
                {/* Hover Actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button size="icon" variant="secondary" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredItems.map(item => (
              <div
                key={item.id}
                className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-all cursor-pointer"
                onClick={() => setSelectedItem(item)}
              >
                <img
                  src={item.thumbnail || item.url}
                  alt={item.title}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <p className="font-medium text-[#1a365d]">{item.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">{item.category}</Badge>
                    {item.tags?.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">#{tag}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="mt-8 text-center text-sm text-[#64748b]">
          {filteredItems.length} média{filteredItems.length > 1 ? 's' : ''} • 
          {items.filter(i => i.type === 'image').length} images • 
          {items.filter(i => i.type === 'video').length} vidéos
        </div>
      </main>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ajouter un média</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                placeholder="Ex: Logo TechCorp v2"
              />
            </div>
            <div>
              <Label>URL du média *</Label>
              <Input
                value={uploadForm.url}
                onChange={(e) => setUploadForm({...uploadForm, url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>URL miniature (optionnel)</Label>
              <Input
                value={uploadForm.thumbnail}
                onChange={(e) => setUploadForm({...uploadForm, thumbnail: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  {MEDIA_CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Type</Label>
                <select
                  value={uploadForm.type}
                  onChange={(e) => setUploadForm({...uploadForm, type: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg p-2"
                >
                  <option value="image">Image</option>
                  <option value="video">Vidéo</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Tags (séparés par virgule)</Label>
              <Input
                value={uploadForm.tags}
                onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                placeholder="tech, startup, logo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>Annuler</Button>
            <Button onClick={handleUpload}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{selectedItem?.title}</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div>
              {selectedItem.type === 'video' ? (
                <div className="aspect-video bg-black rounded-lg">
                  <video src={selectedItem.url} controls className="w-full h-full" />
                </div>
              ) : (
                <img src={selectedItem.url} alt={selectedItem.title} className="w-full rounded-lg" />
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                {selectedItem.tags?.map(tag => (
                  <Badge key={tag} variant="secondary">#{tag}</Badge>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
